import PropTypes from "prop-types";
import { useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { PlusIcon } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import {
  BLANK_ADDRESS,
  isAddressComplete,
} from "components/BaseForm/addresses/helpers";
import FormAddressStreet from "components/BaseForm/fields/FormAddressStreet";
import FormCountrySelect from "components/BaseForm/fields/FormCountrySelect";
import FormInput from "components/BaseForm/fields/FormInput";
import RemoveButton from "components/BaseForm/fields/RemoveButton";
import SectionCard from "components/BaseForm/SectionCard";
import { formatAddress } from "helpers/address";

const AddressFields = ({ index, showCoordinates }) => {
  const prefix = `addresses.${index}`;
  // The accordion panel clips its overflow for the open animation, so the
  // inset keeps the inputs' focus rings inside it.
  return (
    <FieldGroup className="tw:grid tw:grid-cols-1 tw:gap-5 tw:p-1 tw:pt-2 tw:md:grid-cols-2">
      <FormAddressStreet name={prefix} label="Street 1" required />
      <FormInput name={`${prefix}.street2`} label="Street 2" />
      <FormInput name={`${prefix}.city`} label="City" required />
      <FormInput name={`${prefix}.postal_code`} label="Postal Code" required />
      <FormInput name={`${prefix}.region`} label="Region" required />
      <FormCountrySelect name={`${prefix}.country`} label="Country" required />
      {showCoordinates ? (
        <>
          <FormInput name={`${prefix}.latitude`} label="Latitude" />
          <FormInput name={`${prefix}.longitude`} label="Longitude" />
        </>
      ) : null}
    </FieldGroup>
  );
};

AddressFields.propTypes = {
  index: PropTypes.number.isRequired,
  showCoordinates: PropTypes.bool,
};

/**
 * The Primary switch. Only one address is primary, and only a complete
 * address can be marked.
 */
const PrimarySwitch = ({ index, isComplete, onPrimaryChange }) => {
  const { getValues, setValue } = useFormContext();
  const isPrimary = useWatch({ name: `addresses.${index}.is_primary` });
  const switchId = `addresses-${index}-primary`;

  const handleCheckedChange = checked => {
    getValues("addresses").forEach((_, i) =>
      setValue(`addresses.${i}.is_primary`, checked && i === index, {
        shouldDirty: true,
      })
    );
    if (checked) onPrimaryChange?.(index);
  };

  return (
    <Field
      orientation="horizontal"
      className="tw:w-auto"
      data-disabled={isComplete ? undefined : true}
    >
      <Switch
        id={switchId}
        checked={Boolean(isPrimary)}
        disabled={!isComplete}
        onCheckedChange={handleCheckedChange}
      />
      <FieldLabel htmlFor={switchId}>Primary</FieldLabel>
    </Field>
  );
};

PrimarySwitch.propTypes = {
  index: PropTypes.number.isRequired,
  isComplete: PropTypes.bool.isRequired,
  onPrimaryChange: PropTypes.func,
};

/**
 * The address row header: its summary, the optional Primary switch, and
 * removal. The switch and the remove button sit beside the accordion trigger,
 * because a button cannot hold other controls.
 */
const AddressItem = ({ index, showPrimary, onPrimaryChange, onRemove }) => {
  const address = useWatch({ name: `addresses.${index}` });
  const isComplete = isAddressComplete(address);

  return (
    <div className="tw:flex tw:items-center tw:gap-3">
      <div className="tw:min-w-0 tw:flex-1">
        {/* min-w-0 lets the trigger shrink below its text, so the summary
            truncates instead of running under the controls beside it. */}
        <AccordionTrigger className="tw:min-w-0 tw:items-center">
          <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
            <span className="tw:truncate">
              {formatAddress(address) || `Address ${index + 1}`}
            </span>
            {isComplete ? null : (
              <Badge variant="destructive" className="tw:shrink-0">
                Missing required fields
              </Badge>
            )}
          </span>
        </AccordionTrigger>
      </div>
      {showPrimary ? (
        <PrimarySwitch
          index={index}
          isComplete={isComplete}
          onPrimaryChange={onPrimaryChange}
        />
      ) : null}
      <RemoveButton label={`Remove address ${index + 1}`} onClick={onRemove} />
    </div>
  );
};

AddressItem.propTypes = {
  index: PropTypes.number.isRequired,
  showPrimary: PropTypes.bool,
  onPrimaryChange: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
};

/**
 * The `addresses` field array as an accordion, one address open at a time.
 * `onPrimaryChange(index)` runs after an address is marked primary.
 */
const AddressesSection = ({
  showPrimary = false,
  showCoordinates = false,
  onPrimaryChange,
}) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "addresses",
  });
  const [openIndex, setOpenIndex] = useState(null);

  const handleAdd = () => {
    append({ ...BLANK_ADDRESS });
    setOpenIndex(fields.length);
  };

  const handleRemove = index => {
    remove(index);
    setOpenIndex(current => {
      if (current === index) return null;
      return current > index ? current - 1 : current;
    });
  };

  return (
    <SectionCard title="Addresses">
      {fields.length > 0 ? (
        <Accordion
          value={openIndex == null ? [] : [openIndex]}
          onValueChange={next => setOpenIndex(next[0] ?? null)}
        >
          {fields.map((field, index) => (
            <AccordionItem key={field.id} value={index}>
              <AddressItem
                index={index}
                showPrimary={showPrimary}
                onPrimaryChange={onPrimaryChange}
                onRemove={() => handleRemove(index)}
              />
              <AccordionContent>
                <AddressFields
                  index={index}
                  showCoordinates={showCoordinates}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <Empty className="tw:border tw:p-4">
          <EmptyHeader>
            <EmptyTitle>No addresses</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}
      <div>
        <Button type="button" variant="outline" onClick={handleAdd}>
          <PlusIcon data-icon="inline-start" />
          Add address
        </Button>
      </div>
    </SectionCard>
  );
};

AddressesSection.propTypes = {
  showPrimary: PropTypes.bool,
  showCoordinates: PropTypes.bool,
  onPrimaryChange: PropTypes.func,
};

export default AddressesSection;
